import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Loader from "../components/common/Loader";
import ErrorState from "../components/common/ErrorState";
import Badge from "../components/common/Badge";
import studentService from "../services/studentService";
import { FILE_BASE_URL } from "../services/api";
import { formatDate, initials } from "../utils/format";

export default function StudentDetails() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [state, setState] = useState("loading");
  const [toggling, setToggling] = useState(false);

  function load() {
    setState("loading");
    studentService
      .get(id)
      .then((res) => {
        setStudent(res.data);
        setState("ready");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, [id]); // eslint-disable-line

  // NOTE: student_details.is_active is an ENUM('1','0') column, so MySQL
  // always returns it as the STRING "1" or "0" — never a real number. In
  // JavaScript, !!"0" is true (any non-empty string is truthy), so this
  // must check Number(...) === 1 instead of relying on truthiness.
  async function toggleStatus() {
    setToggling(true);
    try {
      await studentService.setStatus(id, Number(student.is_active) === 1 ? 0 : 1);
      load();
    } catch (_) {
    } finally {
      setToggling(false);
    }
  }

  if (state === "loading") return <div className="pm-page"><Loader label="Loading student" /></div>;
  if (state === "error") return <div className="pm-page"><ErrorState onRetry={load} /></div>;

  return (
    <div className="pm-page">
      <Link to="/admin/students" className="pm-back-link">← All students</Link>

      <header className="pm-page__header">
        <div className="pm-student-header">
          <div className="pm-avatar">{initials(student.name)}</div>
          <div>
            <span className="pm-eyebrow">{student.university_name}</span>
            <h1>{student.name}</h1>
          </div>
        </div>
        <div className="pm-page__actions">
          <Badge active={Number(student.is_active) === 1} />
          <button className="pm-btn pm-btn--ghost" disabled={toggling} onClick={toggleStatus}>
            {Number(student.is_active) === 1 ? "Deactivate" : "Activate"}
          </button>
        </div>
      </header>

      <div className="pm-detail-grid">
        <div className="pm-card">
          <h3>Contact</h3>
          <dl className="pm-dl">
            <div><dt>Student ID</dt><dd>{student.student_id}</dd></div>
            <div><dt>Email</dt><dd>{student.email_id}</dd></div>
            <div><dt>Applied</dt><dd>{formatDate(student.applied_date)}</dd></div>
          </dl>
        </div>

        <div className="pm-card">
          <h3>Campus</h3>
          <dl className="pm-dl">
            <div><dt>University</dt><dd>{student.university_name}</dd></div>
            <div><dt>Location</dt><dd>{student.location || "—"}</dd></div>
            <div><dt>Hiring type</dt><dd>{student.type_of_hire || "—"}</dd></div>
            <div><dt>Window</dt><dd>{formatDate(student.start_date)} – {formatDate(student.end_date)}</dd></div>
          </dl>
        </div>

        <div className="pm-card">
          <h3>Academics</h3>
          <dl className="pm-dl">
            <div><dt>Qualification</dt><dd>{student.education_qualification || "—"}</dd></div>
            <div><dt>Specialisation</dt><dd>{student.specialisation || "—"}</dd></div>
            <div><dt>10th CGPA</dt><dd>{student["10_percentage"] ?? "—"}</dd></div>
            <div><dt>12th CGPA</dt><dd>{student["12_percentage"] ?? "—"}</dd></div>
            <div><dt>Current CGPA</dt><dd>{student.current_cgpa ?? "—"}</dd></div>
            <div><dt>Undergrad CGPA</dt><dd>{student.undergrad_cgpa ?? "—"}</dd></div>
            <div><dt>Passing year</dt><dd>{student.education_pass_yr || "—"}</dd></div>
          </dl>
        </div>

        <div className="pm-card">
          <h3>Full-time work experience</h3>
          {student.has_work_experience === "yes" ? (
            <dl className="pm-dl">
              <div><dt>Years</dt><dd>{student.work_experience_years ?? "—"}</dd></div>
              <div><dt>Organizations</dt><dd style={{ textAlign: "left", whiteSpace: "pre-wrap" }}>{student.work_experience_organizations || "—"}</dd></div>
            </dl>
          ) : (
            <p className="pm-body-text">No full-time work experience.</p>
          )}
        </div>

        <div className="pm-card">
          <h3>Documents</h3>
          <div className="pm-doc-list">
            <a
              className={`pm-doc-link ${!student.cv ? "is-disabled" : ""}`}
              href={student.cv ? `${FILE_BASE_URL}/${student.cv}` : undefined}
              target="_blank"
              rel="noreferrer"
            >
              📄 CV / Resume {student.cv ? "" : "(not uploaded)"}
            </a>
            <a
              className={`pm-doc-link ${!student.video ? "is-disabled" : ""}`}
              href={student.video ? `${FILE_BASE_URL}/${student.video}` : undefined}
              target="_blank"
              rel="noreferrer"
            >
              🎬 Introduction video {student.video ? "" : "(not uploaded)"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}